// Admin · People. This file and server/admin_people.go are the whole section.
//
// WHAT IT ANSWERS. Who has an account, under which client, and whether a paying
// client is actually signing in. The client list comes from the data — TDI-Brooks
// is the first client, not a special case — so a second client appears here the
// day its first row exists, with no edit to this file.
//
// LAST SIGN-IN, NOT LAST SEEN. The sessions table records when a session was
// opened and nothing about activity within it, so the column says "Last sign-in"
// and the note under each table says what that does and does not cover. Naming it
// "last seen" would claim a measurement we do not take.
//
// ONE ACTION: switch an account off, or back on. It is confirmed by name because
// the person on the other end may be offshore, and it deletes nothing.

interface Person {
  id: number;
  name: string;
  email: string;
  /** Carried but not displayed: every account is 'member' until Phase 1 of
   *  docs/ACCOUNTS.md gives this column real values. It is NOT admin-area
   *  access and never was — see 0017_no_client_admins.sql. */
  role: string;
  /** A SeaState principal — the only field here that crosses the client
   *  boundary, and the one that decides who can see this page. */
  staff: boolean;
  created_at: string;
  /** Most recent session opened; null when none is on record. */
  last_sign_in: string | null;
  /** When access was switched off; null when the account is active. */
  disabled_at: string | null;
  /** The signed-in admin's own row. */
  self: boolean;
}

interface ClientGroup {
  id: number;
  name: string;
  people: Person[];
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// ---- parsing ----------------------------------------------------------------
// The payload is our own, but it arrives as unknown and is narrowed here rather
// than asserted: a shape change should degrade to "could not read" and not to a
// page of undefineds.

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function nullableStr(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

function toPerson(v: unknown): Person | null {
  if (!isRecord(v) || typeof v.id !== "number") return null;
  return {
    id: v.id,
    name: str(v.name),
    email: str(v.email),
    role: str(v.role),
    staff: v.staff === true,
    created_at: str(v.created_at),
    last_sign_in: nullableStr(v.last_sign_in),
    disabled_at: nullableStr(v.disabled_at),
    self: v.self === true,
  };
}

function toGroups(v: unknown): ClientGroup[] | null {
  if (!isRecord(v) || !Array.isArray(v.clients)) return null;
  const out: ClientGroup[] = [];
  for (const raw of v.clients) {
    if (!isRecord(raw) || typeof raw.id !== "number") return null;
    const people: Person[] = [];
    if (Array.isArray(raw.people)) {
      for (const p of raw.people) {
        const person = toPerson(p);
        if (person !== null) people.push(person);
      }
    }
    out.push({ id: raw.id, name: str(raw.name), people });
  }
  return out;
}

// ---- time -------------------------------------------------------------------

function parseTime(iso: string | null): Date | null {
  if (iso === null || iso === "") return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Absolute, local, unambiguous — the tooltip behind every relative stamp. */
function fullStamp(d: Date): string {
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shortDay(d: Date): string {
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

/** How long ago, at the coarsest unit that is still honest. */
function ago(d: Date, now: number): string {
  const s = Math.round((now - d.getTime()) / 1000);
  if (s < 0) return "just now";
  if (s < 90) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 36) return `${h} h ago`;
  const days = Math.round(h / 24);
  if (days < 45) return `${days} d ago`;
  const months = Math.round(days / 30.4);
  if (months < 24) return `${months} mo ago`;
  return `${Math.round(days / 365)} y ago`;
}

// ---- cells ------------------------------------------------------------------

function whenCell(iso: string | null, unknownText: string): HTMLTableCellElement {
  const td = el("td", "mono");
  const d = parseTime(iso);
  if (d === null) {
    const span = el("span", "ad-when", unknownText);
    td.appendChild(span);
    return td;
  }
  const span = el("span", "ad-when", ago(d, Date.now()));
  span.title = fullStamp(d);
  td.appendChild(span);
  return td;
}

function accessCell(p: Person): HTMLTableCellElement {
  const td = el("td");
  const disabled = parseTime(p.disabled_at);
  if (disabled === null) {
    td.appendChild(el("span", "ad-state ok", "Active"));
    return td;
  }
  const span = el("span", "ad-state", "Disabled");
  span.title = `Disabled ${fullStamp(disabled)}`;
  td.appendChild(span);
  return td;
}

// ---- section ----------------------------------------------------------------

export function mountAdminPeople(host: HTMLElement): void {
  const status = el("span", "ad-state", "");
  const reload = el("button", "btn", "Reload");
  const toolbar = el("div", "ad-toolbar");
  const spacer = el("span", "ad-spacer");
  toolbar.append(reload, spacer, status);

  const list = el("div", "admin-host");
  host.append(toolbar, list);

  let groups: ClientGroup[] = [];

  const setStatus = (text: string, kind: "" | "ok" | "bad"): void => {
    status.className = kind === "" ? "ad-state" : `ad-state ${kind}`;
    status.textContent = text;
    // An empty state still draws its dot; hide the element when it says nothing.
    status.style.display = text === "" ? "none" : "";
  };

  const oneCard = (title: string, body: HTMLElement): HTMLElement => {
    const card = el("div", "ad-card");
    card.append(el("h2", undefined, title), body);
    return card;
  };

  /** Turn one account's access on or off, then repaint that row's card. */
  const setAccess = async (person: Person, disable: boolean, button: HTMLButtonElement) => {
    button.disabled = true;
    const was = button.textContent ?? "";
    button.textContent = "Working…";
    setStatus("", "");
    try {
      const res = await fetch(`/api/admin/people/${person.id}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ disabled: disable }),
      });
      const payload: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = isRecord(payload) ? str(payload.error) : "";
        setStatus(msg !== "" ? msg : `Could not change access (HTTP ${res.status}).`, "bad");
        button.disabled = false;
        button.textContent = was;
        return;
      }
      const updated = toPerson(payload);
      if (updated === null) {
        setStatus("The server's reply could not be read. Reload to see the current state.", "bad");
        button.disabled = false;
        button.textContent = was;
        return;
      }
      for (const g of groups) {
        const i = g.people.findIndex((p) => p.id === updated.id);
        if (i >= 0) g.people[i] = updated;
      }
      setStatus(
        `${updated.name} is now ${updated.disabled_at === null ? "active" : "disabled"}.`,
        "ok",
      );
      paint();
    } catch {
      setStatus("Could not reach the server.", "bad");
      button.disabled = false;
      button.textContent = was;
    }
  };

  const personRow = (p: Person): HTMLTableRowElement => {
    const tr = el("tr", p.disabled_at === null ? undefined : "muted");

    tr.appendChild(el("td", undefined, p.name));
    tr.appendChild(el("td", "mono", p.email));
    // One question, not two. `role` has no meaning yet — every account is
    // 'member' until Phase 1 of docs/ACCOUNTS.md gives the column real values —
    // and printing a word that means nothing is how "admin" came to look like an
    // answer. The only distinction that exists today is us or the client.
    const whoCell = el("td");
    whoCell.appendChild(
      p.staff ? el("span", "ad-staff", "SeaState") : el("span", "ad-when", "client"),
    );
    tr.appendChild(whoCell);

    const created = parseTime(p.created_at);
    const createdCell = el("td", "mono");
    const createdSpan = el("span", "ad-when", created === null ? "unknown" : shortDay(created));
    if (created !== null) createdSpan.title = fullStamp(created);
    createdCell.appendChild(createdSpan);
    tr.appendChild(createdCell);

    // "unknown", never a stand-in: an account that has never opened a session
    // and one signed into this morning must not read the same.
    tr.appendChild(whenCell(p.last_sign_in, "unknown"));
    tr.appendChild(accessCell(p));

    const action = el("td");
    if (p.self) {
      // No button at all: the server refuses to disable the caller, and an
      // admin locked out of the admin area has no way back in through the app.
      action.appendChild(el("span", "ad-when", "you"));
    } else {
      const disabled = p.disabled_at !== null;
      const btn = el("button", "btn", disabled ? "Enable" : "Disable");
      btn.type = "button";
      btn.addEventListener("click", () => {
        if (!disabled) {
          const ok = window.confirm(
            `Disable ${p.name} (${p.email})?\n\n` +
              `They lose access to SeaState. Nothing is deleted, and you can turn ` +
              `access back on here at any time.`,
          );
          if (!ok) return;
        }
        void setAccess(p, !disabled, btn);
      });
      action.appendChild(btn);
    }
    tr.appendChild(action);

    return tr;
  };

  const clientCard = (g: ClientGroup): HTMLElement => {
    const count = g.people.length;
    const off = g.people.filter((p) => p.disabled_at !== null).length;
    const parts = [`${count} ${count === 1 ? "account" : "accounts"}`];
    if (off > 0) parts.push(`${off} disabled`);
    const title = `${g.name} — ${parts.join(", ")}`;

    if (count === 0) {
      return oneCard(title, el("div", "ad-empty", "No accounts under this client yet."));
    }

    const body = el("div");
    const table = el("table", "ad-table");
    const thead = el("thead");
    const hrow = el("tr");
    for (const h of ["Name", "Email", "Who", "Added", "Last sign-in", "Access", ""]) {
      hrow.appendChild(el("th", undefined, h));
    }
    thead.appendChild(hrow);
    const tbody = el("tbody");
    for (const p of g.people) tbody.appendChild(personRow(p));
    table.append(thead, tbody);
    body.appendChild(table);

    // The caveat travels with the number, on the same card as the column.
    body.appendChild(
      el(
        "div",
        "ad-empty",
        "Last sign-in is when a session was opened, not the last time the console was " +
          "used. Signing out and expiry remove the session, so it reads unknown when no " +
          "session is on record.",
      ),
    );
    return oneCard(title, body);
  };

  function paint(): void {
    list.replaceChildren();
    if (groups.length === 0) {
      list.appendChild(oneCard("People", el("div", "ad-empty", "No clients yet.")));
      return;
    }
    for (const g of groups) list.appendChild(clientCard(g));
  }

  const load = async (): Promise<void> => {
    reload.disabled = true;
    list.replaceChildren(oneCard("People", el("div", "ad-empty", "Loading accounts…")));
    try {
      const res = await fetch("/api/admin/people", { credentials: "same-origin" });
      const payload: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = isRecord(payload) ? str(payload.error) : "";
        list.replaceChildren(
          oneCard(
            "People",
            el(
              "div",
              "ad-empty",
              msg !== "" ? `Could not load accounts: ${msg}` : `Could not load accounts (HTTP ${res.status}).`,
            ),
          ),
        );
        return;
      }
      const parsed = toGroups(payload);
      if (parsed === null) {
        list.replaceChildren(
          oneCard("People", el("div", "ad-empty", "The accounts payload could not be read.")),
        );
        return;
      }
      groups = parsed;
      paint();
    } catch {
      list.replaceChildren(
        oneCard("People", el("div", "ad-empty", "Could not reach the server.")),
      );
    } finally {
      reload.disabled = false;
    }
  };

  reload.type = "button";
  reload.addEventListener("click", () => {
    setStatus("", "");
    void load();
  });

  setStatus("", "");
  void load();
}
